import { createSelector } from 'reselect';
import type { State as DashboardState } from '../reducers';
import { toPercentage, toFactor } from '@/utilities/formatting';
import { getAverageEntities } from '@ducks/averages/selectors';
import { getPath } from '@/router/utils';
import { ROUTE_STOCKS_MARKET } from '@/constants/routes';
import { slugify } from '@/utilities/slugify';

const formatterPercentage = (value: number | null) =>
  value !== null ? toPercentage(value, 1) : 'n/a';

const formatterFactor = (value: number | null) =>
  value !== null ? toFactor(value, 1) : 'n/a';

export interface Entity {
  marketISO: string;
  industrySlug?: string;
  industryName?: string;
  canonicalUrl?: string;
  return7D: number | null;
  return7DFormatted: string;
  return1YrAbs: number | null;
  return1YrAbsFormatted: string;
  PERatio: number | null;
  PERatioFormatted: string;
}

const getMyMarketAndIndustryKeys = ({
  dashboard: { myMarketKeys, myIndustryKeys },
}: {
  dashboard: DashboardState;
}) => ({ myMarketKeys, myIndustryKeys });

export const getMyMarketAndIndustryEntitiesSelector = createSelector(
  [getMyMarketAndIndustryKeys, getAverageEntities],
  ({ myMarketKeys, myIndustryKeys }, averages): Record<string, Entity> => {
    const entities: Record<string, Entity> = {};
    myMarketKeys.forEach((n) => {
      const [marketISO] = JSON.parse(n);
      const { return7d, return1yrAbs, PE } = averages[marketISO].market;
      const PERatio = typeof PE === 'undefined' ? null : PE.value;
      const return7D = typeof return7d === 'undefined' ? null : return7d.value;
      const return1YrAbs =
        typeof return1yrAbs === 'undefined' ? null : return1yrAbs.value;
      entities[n] = {
        marketISO,
        canonicalUrl: getPath({
          type: ROUTE_STOCKS_MARKET,
          payload: { market: marketISO },
        }),
        return7D,
        return7DFormatted: formatterPercentage(return7D),
        return1YrAbs,
        return1YrAbsFormatted: formatterPercentage(return1YrAbs),
        PERatio,
        PERatioFormatted: formatterFactor(PERatio),
      };
    });
    myIndustryKeys.forEach((n) => {
      const [marketISO, industryId, industry] = JSON.parse(n);
      const { return7d, return1yrAbs, PE } =
        averages[marketISO].industry[industryId];
      const PERatio = typeof PE === 'undefined' ? null : PE.value;
      const return7D = typeof return7d === 'undefined' ? null : return7d.value;
      const return1YrAbs =
        typeof return1yrAbs === 'undefined' ? null : return1yrAbs.value;
      entities[n] = {
        marketISO,
        industryName: industry,
        industrySlug: slugify(industry),
        return7D,
        return7DFormatted: formatterPercentage(return7D),
        return1YrAbs,
        return1YrAbsFormatted: formatterPercentage(return1YrAbs),
        PERatio,
        PERatioFormatted: formatterFactor(PERatio),
      };
    });
    return entities;
  }
);

export const getMyMarketKeysSelector = createSelector(
  [getMyMarketAndIndustryKeys],
  ({ myMarketKeys }): string[] => myMarketKeys
);

export const getMyIndustryKeysSelector = createSelector(
  [getMyMarketAndIndustryKeys],
  ({ myIndustryKeys }): string[] => myIndustryKeys
);
